import React, { useState, useEffect } from "react";
import "./MediaSlider.css";

const MediaSlider = ({ mediaArray, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Esc key press close
  useEffect(() => {
    const handleKeyDown = (e) => {
      console.log("Pressed:", e.key);

      if (e.key === "Escape") {
        // console.log("ESC pressed");
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Filter and categorize media
  const validMedia = mediaArray.filter((media) => {
    if (!media) return false;

    const url = typeof media === "string" ? media : media.url || media.src;
    if (!url) return false;

    // Check if it's an image
    if (/\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(url)) return true;

    // Check if it's a video
    if (/\.(mp4|webm|ogg|mov|avi)$/i.test(url)) return true;

    // Check for common image/video URL patterns
    if (
      url.includes("youtube.com") ||
      url.includes("youtu.be") ||
      url.includes("vimeo.com") ||
      url.includes("cloudinary") ||
      url.includes("amazonaws.com") ||
      url.includes("googleapis.com")
    ) {
      return true;
    }

    return false;
  });

  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === validMedia.length - 1 ? 0 : prevIndex + 1,
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? validMedia.length - 1 : prevIndex - 1,
    );
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Auto-play functionality (optional)
  //   useEffect(() => {
  //     if (validMedia.length <= 1) return;

  //     const timer = setInterval(() => {
  //       nextSlide();
  //     }, 5000);

  //     return () => clearInterval(timer);
  //   }, [currentIndex, validMedia.length]);

  if (!validMedia || validMedia.length === 0) {
    return (
      <div className="media-slider-overlay">
        <div className="media-slider-container">
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
          <div className="no-media-message">No media available</div>
        </div>
      </div>
    );
  }

  const getMediaType = (media) => {
    const url = typeof media === "string" ? media : media.url || media.src;

    // Check for images
    if (/\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(url)) return "image";

    // Check for videos
    if (/\.(mp4|webm|ogg|mov|avi)$/i.test(url)) return "video";

    // Check for YouTube
    if (url.includes("youtube.com") || url.includes("youtu.be"))
      return "youtube";

    // Check for Vimeo
    if (url.includes("vimeo.com")) return "vimeo";

    // Default to image for unknown types (common with CDN URLs)
    return "image";
  };

  const renderCurrentMedia = () => {
    const currentMedia = validMedia[currentIndex];
    const mediaType = getMediaType(currentMedia);
    const mediaUrl =
      typeof currentMedia === "string"
        ? currentMedia
        : currentMedia.url || currentMedia.src;

    switch (mediaType) {
      case "image":
        return (
          <img
            src={mediaUrl}
            alt={`Slide ${currentIndex + 1}`}
            className="media-content"
            loading="lazy"
          />
        );

      case "video":
        return (
          <video controls className="media-content">
            <source
              src={mediaUrl}
              type={`video/${mediaUrl.split(".").pop()}`}
            />
            Your browser does not support the video tag.
          </video>
        );

      case "youtube":
        const youtubeId = mediaUrl.match(
          /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/,
        );
        if (youtubeId) {
          return (
            <iframe
              src={`https://www.youtube.com/embed/${youtubeId[1]}`}
              className="media-content"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="YouTube video"
            />
          );
        }
        break;

      case "vimeo":
        const vimeoId = mediaUrl.match(
          /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)([0-9]+)/,
        );
        if (vimeoId) {
          return (
            <iframe
              src={`https://player.vimeo.com/video/${vimeoId[1]}`}
              className="media-content"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title="Vimeo video"
            />
          );
        }
        break;

      default:
        return <div className="unsupported-media">Unsupported media type</div>;
    }
  };

  return (
    <div className="media-slider-overlay">
      <div className="media-slider-container">
        {/* Close Button */}
        <button className="close-btnm" onClick={onClose}>
          ×
        </button>

        {/* Navigation Arrows */}
        {validMedia.length > 1 && (
          <>
            <button className="nav-btn prev-btn" onClick={prevSlide}>
              ‹
            </button>
            <button className="nav-btn next-btn" onClick={nextSlide}>
              ›
            </button>
          </>
        )}

        {/* Current Media */}
        <div className="media-display">{renderCurrentMedia()}</div>

        {/* Slide Indicators */}
        {validMedia.length > 1 && (
          <div className="slide-indicators">
            {validMedia.map((_, index) => (
              <button
                key={index}
                className={`indicator ${
                  index === currentIndex ? "active" : ""
                }`}
                onClick={() => goToSlide(index)}
              />
            ))}
          </div>
        )}

        {/* Slide Counter */}
        {validMedia.length > 1 && (
          <div className="slide-counter">
            {currentIndex + 1} / {validMedia.length}
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaSlider;
